<template>
  <Modal
      centered
      :visible="visible"
      :title="null"
      :animated="false"
      :footer="null"
      :width="800"
      wrap-class-name="invite-more-modal-wrapper"
      :afterClose="handleReset"
      @change="$emit('change', $event)"
      destroy-on-close
  >
    <div class="left-area">
      <FormModel class="input-container" ref="formRef" :model="formModel" >
        <FormModelItem prop="key">
          <Input
              v-model="formModel.key"
              :class="searchClass"
              :placeholder="$t('home.AddPhoneAndUsername')"
              @blur="searchBlur"
              @focus="searchFocus"
              allow-clear
              autoFocus
          >
            <SvgIcon slot="prefix" icon-class="search" class-name="search"/>
          </Input>
        </FormModelItem>
      </FormModel>
      <SessionSearchResult
        :searchVal="formModel.key"
        v-if="formModel.key.trim()"
      ></SessionSearchResult>
      <div class="trees" v-else>
        <UserCompanyTree
          class="tree-container"
          :checkable="true"
          :tree-data="processedTreeData"
          :treeExpandedList="treeExpandedList"
          @check="treeCheck"
        />
      </div>
    </div>
    <div class="right-area">
      <div class="header" v-if="selectedAtLeaseOne">{{$t('home.ContactsSelected').replace('XX', selectedMembers.length)}}</div>
      <div class="selected">
        <div class="selectedMemberInfo"
             v-for="(member, index) in selectedMembers"
             :key="index"
        >
          <div class="avatar">
            <UserAvatar
              style="width: 20px;height: 20px;margin-right: 8px;"
              :user-avatar="member.avatarUrl||member.avatar"
            />
            {{member.name}}
          </div>
          <div
            v-if="showClose(member)"
            class="close"
            @click="removeSelectedMember(member)"
            @mouseenter="enterClose(member, $event)"
            @mouseleave="leaveClose($event)"
          ></div>
        </div>
      </div>
      <div class="buttons">
        <BasicBtnGroup
            @primeClick="onOk"
            @defClick="onCancel"
            class="basic-btns"
        />
      </div>
    </div>
  </Modal>
</template>
<script>
import { Modal, FormModel, Input } from 'ant-design-vue'
import SvgIcon from '@/components/behavior/svg-icon/index.vue'
import BasicBtnGroup from '@/components/behavior/basic-btn-group/index.vue'
import UserCompanyTree from '@/components/business/user-company-tree'
import UserAvatar from '@/components/business/avatar/UserAvatar.vue'
import SessionSearchResult from '@/components/business/session-search-result'

import { mapState, mapMutations, mapActions, mapGetters } from 'vuex'
import axios from 'axios'
import { isEmpty, clone, differenceWith } from 'ramda'
import { userMapperInfo } from '@/utils/userMapperInfo'

/**
 * 处理需要disableCheckbox的树形结构数据
 * @param treeData 树形结构数据
 * @param disableList 需要disabled的list数据
 */
function handleDisableCheckboxTree(treeData, disableList) {
  for (let i = 0; i < treeData.length; i++) {
    for (let j = 0; j < disableList.length; j++) {
      if (treeData[i].orgId === disableList[j]) {
        treeData[i].disableCheckbox = true
      }
    }
    handleDisableCheckboxTree(treeData[i].orgList, disableList)
  }
  return treeData
}

/**
 * 查找某个节点的所有父节点
 * @param tree tree结构
 * @param func 条件判断的回调
 * @param array 符合条件的数组
 * @returns {*[]|[]|*} 数组结果
 */
function findParent(tree, func, array = []) {
  if (!tree) return []
  for (const data of tree) {
    array.push(data.orgId)
    if (func(data)) return array
    if (data.orgList) {
      const findChildren = findParent(data.orgList, func, array)
      if (findChildren.length) return findChildren
    }
    array.pop()
  }
  return []
}

// 邀请协作方
export default {
  name: 'InviteMoreModal',
  components: {
    Modal,
    Input,
    FormModelItem: FormModel.Item,
    FormModel,
    BasicBtnGroup,
    SvgIcon,
    UserCompanyTree,
    UserAvatar,
    SessionSearchResult,
  },
  model: {
    prop: 'visible',
    event: 'change',
  },
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    type: {
      type: String,
      default: 'call',
      validator: function (value) {
        return ['call', 'cooperation'].indexOf(value) !== -1
      }
    },
    // passSelectedMembers依赖于type属性，若为cooperation则必传
    // uid 数组集合 e.g. ['uid1', 'uid2', 'uid3', ...]
    passSelectedMembers: {
      type: Array,
      default: () => ([])
    },
    isHost: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      className: 'close-filled-disabled',
      searchClass: ['account-input'],
      formModel: this.resetForm(),
      loading: false
    }
  },
  watch: {
    visible(nv) {
      if (nv) {
        // 每次打开弹窗需要重新获取最新的联系人信息，这是刻意为之
        // 联系人有可能随时注销
        this.getCompanyTree()
          .then(_ => {
            this.resetState()
            this.setFriendsSelectedMembers(this.initSelectedMembers())
          })
      }
    }
  },
  methods: {
    ...mapActions(['inviteMemberJoinRoom', 'createAndStartImmediateConference']),
    ...mapActions('collaborator', ['getCompanyTree']),
    ...mapMutations('sessionAdd', ['setFriendsSelectedMembers', 'resetState', 'spliceFriendsSelectedMember', 'spliceSelectedCrop', 'setSelectedTreeOrgan', 'spliceSelectedTreeOrgan', 'spliceNotFriendsSelectedMember']),
    async onOk(){
      // 呼叫原逻辑
      if (this.type === 'call') {
        // selectedMembers始终包含自己
        if (this.selectedMembers.length <= 1) {
          return this.$message.error(this.$t('home.Less1Member'))
        }
        // 如果没有conferenceId则默认需要先创建
        // 场景：协作列表立即呼叫
        if (!this.currentSession) {
          if (this.loading) return
          this.loading = true
          // 过滤掉本人，因为createAndStartImmediateConference中已经添加了本人
          const selectedMembers = this.selectedMembers
            .filter(el => el.uid !== this.userInfo.id)
            .map(el => {
              return {
                corpId: el.corpId,
                userId: el.uid,
                nickName: el.name
              }
            })
          try {
            await this.createAndStartImmediateConference(selectedMembers)
          } catch (error) {
            console.error('创建并开始协作失败', error)
            this.loading = false
          }
          //  已经开启的会话房间再添加成员
          //  场景：通讯录添加成员呼叫后，再中途添加成员
          //  中途呼叫成员时，需要剔除已经在会话房间里面的成员，只添加新增的成员
        } else {
          const members = this.selectedMembers
            .filter(({ uid }) => !this.disableCheckboxList.find(disabledId => ~disabledId.indexOf(uid)))
            .map(({ corpId, uid, name }) => ({
              corpId,
              userId: uid,
              nickName: name
            }))
          const addMembers = differenceWith((a, b) => a.userId === b.id, members, this.sortedMembers)
          if (!addMembers.length) {
            return this.$message.warn(this.$t('home.ConfirmHasMember'))
          }
          const payload = {
            conferenceId: this.currentSession.srcId,
            members: addMembers,
          }
          const response = await axios.post('/hiapigw/app/conference/members/add', payload)
          if (response.data.code === 0) {
            const conferenceInfo = {
              roomId: this.currentSession.roomId,
              roomKey: this.currentSession.roomKey,
              conferenceId: this.currentSession.srcId,
              members
            }
            this.inviteMemberJoinRoom(conferenceInfo)
            this.$emit('change', false)
          } else {
            this.$message.error(response.data.msg)
          }
        }
      } else {
        //  预约另外一套逻辑
        this.$emit('inviteConfirm', this.selectedMembers)
        this.$emit('change', false)
      }
    },
    onCancel(){
      this.loading = false
      this.$emit('change', false)
    },
    handleReset() {
      this.loading = false
      this.formModel = this.resetForm()
    },
    resetForm() {
      return { key: '' }
    },
    treeCheck(selectedKeys) {
      const filteredUser = selectedKeys.filter(el => typeof el === 'string')
      const filteredOrgan = selectedKeys.filter(el => typeof el === 'number')
      this.setFriendsSelectedMembers(userMapperInfo(filteredUser, true))
      this.setSelectedTreeOrgan(filteredOrgan)
    },
    enterClose(member, e) {
      const id = member.id || member.uid
      e.target.className = !this.disableCheckboxList.includes(`uid|${id}`)
        ? 'close active'
        : 'close'
    },
    leaveClose(e) {
      e.target.className = 'close'
    },
    showClose(member) {
      const id = member.id || member.uid
      return !this.disableCheckboxList.includes(`uid|${id}`)
    },
    removeSelectedMember(member) {
      // 删除成员是好友
      if (this.friendsSelectedMembers.some(el => el.uid === member.uid)) {
        const removeMemberIndex = this.friendsSelectedMembers.findIndex(el => el.uid === member.uid)
        const removeCropIndex = this.selectedCorps.findIndex(el => el.corpId === member.corpId)
        this.spliceFriendsSelectedMember(removeMemberIndex)
        this.spliceSelectedCrop(removeCropIndex)
        const uid = typeof member.uid === 'string' ? `uid|${member.uid}` : member.uid
        findParent(this.processedTreeData, data => data.orgId === uid)
          .filter(el => typeof el === 'number')
          .forEach(ele => {
            this.spliceSelectedTreeOrgan(this.selectedTreeOrgan.findIndex(el => ele === el))
          })
      } else {
        // 删除成员非自己好友
        this.spliceNotFriendsSelectedMember(this.notFriendsSelectedMembers.findIndex(el => el.uid === member.uid))
      }
    },
    initSelectedMembers() {
      // 邀请成员分为两种，一种是呼叫，另一种是预约
      const userId = this.type === 'call'
        ? this.sortedMembers.map(el => el.id)
        : this.passSelectedMembers
      // 初始化状态把自己作为一个选择联系人，不可点击和修改
      if (this.userInfo.id && !userId.includes(this.userInfo.id)) {
        userId.unshift(this.userInfo.id)
      }
      return userMapperInfo(userId, false)
    },
    searchFocus() {
      this.searchClass.push('active')
    },
    searchBlur() {
      this.searchClass.pop()
    }
  },
  computed: {
    ...mapGetters(['sortedMembers']),
    ...mapState(['currentSession', 'userInfo']),
    ...mapGetters('sessionAdd', ['selectedMembers']),
    ...mapState('collaborator', ['treeData', 'hasLoadTree']),
    ...mapState('sessionAdd', ['selectedCorps', 'selectedTreeOrgan', 'friendsSelectedMembers', 'notFriendsSelectedMembers']),
    processedTreeData() {
      return handleDisableCheckboxTree(clone(this.treeData), this.disableCheckboxList)
    },
    selectedAtLeaseOne(){
      return !isEmpty(this.selectedMembers)
    },
    disableCheckboxList() {
      const mapId = this.type === 'call'
        ? this.sortedMembers.map(el => `uid|${el.id}`)
        : this.isHost
          ? []
          : this.passSelectedMembers.map(el => `uid|${el}`)
      return [...new Set(mapId.concat(`uid|${this.userInfo.id}`))]
    },
    // 控制tree的展开/收起状态需要找到所有的父节点，不能包括用户维度
    // 1、以人的维度找到所有的父节点 2、将所有的父节点取并集 3、剔除typeof为string的数据
    treeExpandedList() {
      // 转化对应到树形结构中，联系人以 uid| 开头
      const expandedList = []
      const expendOrigin = this.type === 'call'
        ? this.sortedMembers
          .map(el => `uid|${el.id}`)
          .concat(`uid|${this.userInfo.id}`)
        : this.passSelectedMembers
          .map(el => `uid|${el}`)
          .concat(`uid|${this.userInfo.id}`)
      expendOrigin.forEach(el => {
        expandedList.push(
          ...findParent(this.treeData, data => data.orgId === el)
            .filter(el => typeof el === 'number')
        )
      })
      return Array.from(new Set(expandedList))
    }
  },
  mounted() {
    // note: 刷新通话界面，必须判断树形结构是否加载，没有加载则需要手动加载
    if (!this.hasLoadTree) {
      this.getCompanyTree()
        .then(_ => {
          this.setFriendsSelectedMembers(this.initSelectedMembers())
        })
    } else {
      this.setFriendsSelectedMembers(this.initSelectedMembers())
    }
  },
  destroyed(){
    this.loading = false
  }
}
</script>
<style lang="less" scoped>

::v-deep .invite-more-modal-wrapper {
  .ant-row.ant-form-item {
    margin-bottom: 16px;
  }
  .ant-modal {

    .ant-modal-content {
      border-radius: 16px;
      box-shadow: 0 5px 30px 0 rgba(89, 132, 219, 0.08);
      border: 1px solid #edeff2;
      overflow: hidden;
      padding: 40px 32px;

      .ant-modal-body {
        height: 520px;
        display: flex;
        padding: 0;
        justify-content: flex-start;
        .left-area {
          flex: 1;
          padding: 0 32px;
          margin-right: 48px;
          .search {
            width: 15px;
            height: 15px;
          }
          .input-container input {
            height: 42px;
            width: 280px;
            line-height: 42px;
            background: #EFF3F8;
            box-shadow: 0 0 10px 0 rgba(151, 171, 199, 0.15);
            border-radius: 8px;
            border-color: transparent;
            border-width: 2px;
            font-size: 16px;
            &:hover,&:focus {
              border-color: #236CFF;
              border-right-width: 2px !important;
            }
            &::placeholder {
              font-size: 16px;
              font-weight: 400;
              color: #6a6d73;
            }
          }
          .input-container .account-input.active{
            .ant-input-prefix {
              display: none;
            }
            input {
              padding-left: 16px;
            }
          }
          .trees {
            padding-top: 16px;
            .tree-container {
              width: 280px;
              height: 440px;
              overflow: auto;
              .ant-tree li {
                font-size: 16px;
                padding: 0;
                margin: 0;

                &.corp-node {
                  &>span.ant-tree-switcher:first-child {
                    display: none;
                  }
                  span.ant-tree-title {
                    font-weight: bold;
                    margin-left: 12px;
                  }
                }
                .ant-tree-child-tree {
                  span.ant-tree-title {
                    font-weight: normal;
                  }
                }

                .ant-tree-switcher {
                  height: 40px;
                  line-height: 40px;
                  padding-top: 0;
                  padding-bottom: 0;
                  margin-bottom: 0;
                }
                .ant-tree-checkbox {
                  height: 40px;
                  padding: 12px 0;
                  .ant-tree-checkbox-inner {
                    border-color: #236CFF;
                  }
                }
                .ant-tree-checkbox-checked {
                  &::after {
                    top: 30%;
                    height: 40%;
                  }
                  .ant-tree-checkbox-inner {
                    border-color: #236CFF;
                    background-color: #236CFF;
                  }
                }
                .ant-tree-checkbox-disabled {
                  .ant-tree-checkbox-inner {
                    background-color: #f5f5f5 !important;
                    border-color: #d9d9d9 !important;
                  }
                }
                .ant-tree-node-content-wrapper {
                  background: transparent;
                  height: 40px;

                  .ant-tree-title, .ant-tree-iconEle {
                    height: 40px;
                    line-height: 40px;
                    .svg-icon, img {
                      width: 20px;
                      height: 20px;
                      margin-top: -4px;
                    }
                    .svg-icon {
                      margin-top: 10px;
                    }
                  }
                }

                each(range(10), {
                  @marginLeft: calc(-18px * @value);
                  @paddingLeft: calc(18px * @value);
                  .org-node-@{value}, .leaf-node-@{value} {
                    margin-left: calc(@marginLeft - 24px);
                    padding-left: calc(@paddingLeft + 24px);
                  }
                  .leaf-node-@{value} {
                    margin-bottom: 2px;
                  }
                  .leaf-node-@{value}:not(.ant-tree-treenode-selected) {
                    margin-left: calc(@marginLeft + 8px - 24px);
                    padding-left: calc(@paddingLeft - 8px + 24px);
                    margin-right: 8px;

                    &:hover {
                      background: #F7F8FA;
                    }
                  }
                })
              }
            }
          }
        }
        .right-area {
          flex: 1;
          padding: 0 32px;
          display: flex;
          flex-direction: column;
          .selected {
            margin-top: 16px;
            flex-grow: 1;
            overflow: auto;
            .selectedMemberInfo {
              width: 288px;
              height: 36px;
              background: #FFFFFF;
              box-shadow: 0px 4px 10px 0px rgba(89, 132, 219, 0.08);
              border-radius: 8px;
              border: 1px solid #EDEFF2;
              display: flex;
              flex-direction: row;
              justify-content: space-between;
              align-items: center;
              padding:0 13px 0 8px;
              margin-bottom: 8px;
              .avatar {
                display: flex;
                align-items: center;
              }
              .close {
                width: 14px;
                height: 14px;
                border-radius: 14px;
                background: url(~@/assets/close-filled-disabled.png) no-repeat center;
                &.active {
                  background-image: url(~@/assets/close-filled-enabled.png);
                  cursor: pointer;
                }
              }
            }
          }
          .buttons {
            width: 288px;
            flex-grow: 0;
          }
        }
      }
    }
  }
}
</style>
